import type { PaymentMethodEnum, ProviderEnum } from './common';
import type { PaymentAction } from '@adyen/adyen-web';
export interface CardDetails {
  bin: string;
  expirationMonth: string;
  expirationYear: string;
  lastFourDigits: string;
  nameOnCard: string;
  cardType: string | undefined;
  countryOfIssuance: string;
  fingerprint: string;
  cardBank: string | undefined;
  cardBrand: string;
}

export interface BillingAddress {
  country: string;
  postal_code: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
}

export interface BillingDetails {
  email: string;
  address: BillingAddress;
  name: string | null;
  phone: string | null;
}

export interface CardNetworks {
  available: string[];
  preferred: string | null;
}

export interface ThreeDSecureUsage {
  supported: boolean;
}

export interface CardWallet {
  type: string;
  google_pay: Record<string, unknown>;
}

export interface StripeCard {
  brand: string;
  country: string;
  exp_month: number;
  exp_year: number;
  fingerprint: string;
  funding: string;
  last4: string;
  networks: CardNetworks;
  three_d_secure_usage: ThreeDSecureUsage;
  wallet: CardWallet;
}

export interface PaymentMethodDetails {
  id: string;
  object: string;
  type: string;
  billing_details: BillingDetails;
  allow_redisplay?: string;
  card: StripeCard;
  created: number;
  customer: string | null;
  livemode: boolean;
  metadata: Record<string, unknown>;
}

export interface FraudDetection {
  forterTokenCookie: string;
}

export interface PaymentParams {
  paymentMethodId: string;
  cardDetails: CardDetails;
  paymentMethodDetails: PaymentMethodDetails;
  shouldSaveCard: boolean;
  fraudDetection: FraudDetection;
}

export interface GooglePaymentDataStructure {
  paymentProvider: string;
  receipt_email: string;
  paymentMethodDisplayName: string;
  paymentMethodId: string;
  params: PaymentParams;
}

export interface StripeTokenCardNetworks {
  preferred: string | null;
}

export interface StripeTokenCard {
  id: string;
  object: string;
  address_city: string | null;
  address_country: string | null;
  address_line1: string | null;
  address_line1_check: string | null;
  address_line2: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_zip_check: string | null;
  brand: string;
  country: string;
  cvc_check: string | null;
  dynamic_last4: string;
  email: string | null;
  exp_month: number;
  exp_year: number;
  funding: string;
  last4: string;
  metadata: Record<string, unknown>;
  name: string | null;
  networks: StripeTokenCardNetworks;
  phone: string | null;
  regulated_status: string;
  tokenization_method: string;
  use: string;
  wallet: unknown | null;
}

export interface StripeTokenResponse {
  id: string;
  object: string;
  card: StripeTokenCard;
  client_ip: string;
  created: number;
  livemode: boolean;
  type: string;
  used: boolean;
}

export interface RequestAddress {
  country: string | null;
  postal_code: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
}

export interface RequestBillingDetails {
  email: string;
  address: RequestAddress;
  name: string | null;
  phone: string | null;
}

export interface CardNetworks {
  available: string[];
  preferred: string | null;
}

export interface ThreeDSecureUsage {
  supported: boolean;
}

export interface CardWallet {
  dynamic_last4: string;
  type: string;
  google_pay: Record<string, unknown>;
}

export interface StripeCardDetails {
  checks: Record<string, unknown>;
  display_brand: string;
  brand: string;
  country: string;
  exp_month: number;
  exp_year: number;
  fingerprint: string;
  funding: string;
  last4: string;
  networks: CardNetworks;
  three_d_secure_usage: ThreeDSecureUsage;
  wallet: CardWallet | null;
}

export interface StripePaymentMethodDetails {
  id: string;
  object: string;
  allow_redisplay: string;
  billing_details: {
    address: {
      city: string | null;
      country: string | null;
      line1: string;
      line2: string | null;
      postal_code: string;
      state: string | null;
    };
    email: string;
    name: string | null;
    phone: string | null;
    tax_id: null;
  };
  card: {
    brand: string;
    checks: {
      address_line1_check: string | null;
      address_postal_code_check: string | null;
      cvc_check: string | null;
    };
    country: string;
    display_brand: string;
    exp_month: number;
    exp_year: number;
    funding: string;
    generated_from: unknown | null;
    last4: string;
    networks: {
      available: string[];
      preferred: string | null;
    };
    regulated_status: string;
    three_d_secure_usage: {
      supported: boolean;
    };
    wallet: unknown | null;
  };
  created: number;
  customer: string | null;
  livemode: boolean;
  radar_options: Record<string, unknown>;
  type: string;
}

export interface RequestCardDetails {
  bin: string;
  nameOnCard: string;
  lastFourDigits: string;
  expirationMonth: string;
  expirationYear: string;
  cardBrand: string;
  fingerprint: string;
  countryOfIssuance: string;
}

export interface FraudDetection {
  forterTokenCookie: string;
}

export interface StripePaymentParams {
  paymentMethodId: string;
  cardDetails: RequestCardDetails;
  paymentMethodDetails: StripePaymentMethodDetails;
  shouldSaveCard: boolean;
  fraudDetection: FraudDetection;
}

export interface BaseCheckoutPaymentRequest<TParams = unknown> {
  paymentProvider: string;
  paymentMethodId: string;
  paymentMethodDisplayName: string;
  receipt_email: string;
  params: TParams;
}

export type StripePaymentRequest = BaseCheckoutPaymentRequest<StripePaymentParams>;

export interface StripeCreditCardRequest {
  paymentProvider: 'stripe';
  receipt_email: string;
  params: StripePaymentParams;
  paymentMethodDisplayName: 'Credit Card';
  paymentMethodId: string;
}

export interface StripeGooglePayRequest extends BaseCheckoutPaymentRequest<StripePaymentParams> {
  paymentProvider: 'stripe';
  paymentMethodDisplayName: 'Google Pay';
}

export interface StripeApplePayRequest extends BaseCheckoutPaymentRequest<StripePaymentParams> {
  paymentProvider: 'stripe';
  paymentMethodDisplayName: 'Apple Pay';
}

export interface GooglePayCardNetworks {
  preferred: string | null;
}

export interface GooglePayCard {
  id: string;
  object: 'card';
  address_city: string | null;
  address_country: string | null;
  address_line1: string | null;
  address_line1_check: string | null;
  address_line2: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_zip_check: string | null;
  brand: string;
  country: string;
  cvc_check: string | null;
  dynamic_last4: string;
  email: string | null;
  exp_month: number;
  exp_year: number;
  funding: string;
  last4: string;
  metadata: Record<string, unknown>;
  name: string | null;
  networks: GooglePayCardNetworks;
  phone: string | null;
  regulated_status: string;
  tokenization_method: string;
  use: string;
  wallet: string | null;
}

export interface GooglePayTokenResponse {
  id: string;
  object: 'token';
  card: GooglePayCard;
  client_ip: string;
  created: number;
  livemode: boolean;
  type: 'card';
  used: boolean;
}

export type PaymentToServerResponse = {
  paymentProvider: string;
  orderId: string;
  stripe?: unknown;
  adyen?: unknown;
  nuvei?: unknown;
};

export interface BraintreeCardDetails {
  bin: string;
  expirationMonth: string;
  expirationYear: string;
  lastFourDigits: string;
  nameOnCard: string;
  cardType: string;
}

export interface BraintreeFraudDetection {
  forterTokenCookie: string;
}

export interface BraintreePaymentParams {
  paymentNonce: string;
  cardHolderName: string;
  shouldSaveCard: boolean;
  cardDetails: BraintreeCardDetails;
  fraudDetection: BraintreeFraudDetection;
}

export interface BraintreeCreditCardRequest extends BaseCheckoutPaymentRequest<BraintreePaymentParams> {
  paymentProvider: 'braintree';
  paymentMethodDisplayName: 'Credit Card';
}

export interface PaypalBillingAddress {
  countryCode: string;
}

export interface PaypalShippingAddress {
  line1: string;
  city: string;
  countryCode: string;
  postalCode: string;
  state: string;
  recipientName: string;
}

export interface PaypalMethodDetails {
  email: string;
  firstName: string;
  lastName: string;
  payerId: string;
  countryCode: string;
  billingAddress: PaypalBillingAddress;
  shippingAddress: PaypalShippingAddress;
}

export interface PaypalPaymentParams {
  paymentNonce: string;
  paymentMethodDetails: PaypalMethodDetails;
  fraudDetection: BraintreeFraudDetection;
}

export enum FinalizeStatus {
  SUCCESS = 'succeeded',
  FAILED = 'failed',
  PENDING = 'pending',
  REDIRECT = 'redirect',
}

export interface CreatePaymentSessionResponse {
  sessionToken: string;
}

export type PayFromExpressCheckout =
  | PaymentMethodEnum.APPLE_PAY
  | PaymentMethodEnum.GOOGLE_PAY
  | PaymentMethodEnum.PAYPAL;

export enum AppchargeErrorType {
  GENERIC = 'GenericError', // Backwards compatibility, remove after sprint 73
  INCORRECT_CVC = 'IncorrectCvc', // Backwards compatibility, remove after sprint 73
  TIMEOUT = 'Timeout', // Backwards compatibility, remove after sprint 73
  EXPIRED_CARD = 'ExpiredCard',
  CARD_NOT_SUPPORTED = 'CardNotSupported',
  INSUFFICIENT_FUNDS = 'InsufficientFunds',
  CONNECTION = 'Connection',
  TRANSACTION_NOT_PERMITTED = 'TransactionNotPermitted',
  SUSPECT_FRAUD = 'SuspectFraud',
  AUTHORIZATION_ERROR = 'AuthorizationError',
  AUTHENTICATION_REQUIRED = 'AuthenticationRequired',
  AUTHENTICATION_NOT_HANDLED = 'AuthenticationNotHandled',
  APPROVE_WITH_ID = 'ApproveWithId',
  CARD_VELOCITY_EXCEEDED = 'CardVelocityExceeded',
  CURRENCY_NOT_SUPPORTED = 'CurrencyNotSupported',
  DUPLICATE_TRANSACTION = 'DuplicateTransaction',
  GENERIC_DECLINE = 'GenericDecline',
  INCORRECT_ADDRESS = 'IncorrectAddress',
  INVALID_ZIP = 'IncorrectZip',
  INVALID_ACCOUNT = 'InvalidAccount',
  INVALID_AMOUNT = 'InvalidAmount',
  INVALID_CVC = 'InvalidCvc',
  INVALID_EXPIRY_DATE = 'InvalidExpiryDate',
  INVALID_NUMBER = 'InvalidNumber',
  INVALID_PIN = 'InvalidPin',
  NOT_PERMITTED = 'NotPermitted',
  PIN_TRY_EXCEEDED = 'PinTryExceeded',
  PROCESSING_ERROR = 'ProcessingError',
  REVOCATION_OF_AUTHORIZATION = 'RevocationOfAuthorization',
  WITHDRAWAL_COUNT_LIMIT_EXCEEDED = 'WithdrawalCountLimitExceeded',
}

export type ResultCode =
  | 'AuthenticationFinished'
  | 'AuthenticationNotRequired'
  | 'Authorised'
  | 'Cancelled'
  | 'ChallengeShopper'
  | 'Error'
  | 'IdentifyShopper'
  | 'PartiallyAuthorised'
  | 'Pending'
  | 'PresentToShopper'
  | 'Received'
  | 'RedirectShopper'
  | 'Refused';

export interface BasePaymentResponse {
  orderId: string;
}

export interface PaymentStatusResponse {
  resultCode: ResultCode;
}

export interface BrainTreePaymentResponse {
  paymentProvider: ProviderEnum.BRAINTREE;
  braintree: {
    status: FinalizeStatus;
    providerPaymentId?: string;
  };
  orderId: string;
  appChargeErrorType?: AppchargeErrorType;
}
export interface AdyenPaymentResponse {
  paymentProvider: ProviderEnum.ADYEN;
  adyen: {
    message: string;
    resultCode: ResultCode;
    status: FinalizeStatus;
    action?: PaymentAction;
    redirectUrl?: string;
  };
  orderId: string;
  appChargeErrorType?: AppchargeErrorType;
}
export interface StripePaymentResponse {
  orderId: string;
  paymentProvider: ProviderEnum.STRIPE;
  stripe: {
    intentId: string;
    clientSecret: string;
    status: FinalizeStatus;
    message?: string;
  };
  appChargeErrorType?: AppchargeErrorType;
}

export interface PaypalPaymentResponse {
  orderId: string;
  paymentProvider: ProviderEnum.PAYPAL;
  paypal: {
    status: FinalizeStatus;
  };
  appChargeErrorType?: AppchargeErrorType;
}
export interface NuveiPaymentResponse {
  sessionToken: string;
  orderId: string;
  paymentProvider: ProviderEnum.NUVEI;
  nuvei: {
    clientSecret: string;
    redirectUrl: string;
    status: FinalizeStatus;
  };
  appChargeErrorType?: AppchargeErrorType;
}
export enum PaymentStatus {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  PENDING = 'pending',
  CANCELLED = 'cancelled',
}

export enum StripePaymentIntentStatus {
  SUCCEEDED = 'succeeded',
  REQUIRES_PAYMENT_METHOD = 'requires_payment_method',
  PROCESSING = 'processing',
  REQUIRES_ACTION = 'requires_action',
  REQUIRES_CONFIRMATION = 'requires_confirmation',
  REQUIRES_CAPTURE = 'requires_capture',
  CANCELED = 'canceled',
}

export interface PaymentHandler {
  poll: (
    clientSecret: string,
    pollPaymentStatus: (redirectResult: string) => Promise<PaymentStatusResponse>,
    paymentMethodId: string,
    isTabClosed: boolean
  ) => Promise<{ status: PaymentStatus; params: Record<string, unknown> }>;
}

export interface PaymentHandlerWithTab extends PaymentHandler {
  setTab?: (tab: Window | null) => void;
  cleanup?: () => void;
}

interface PaymentOption {
  userPaymentOptionId: string;
  card: Record<string, unknown>;
  alternativePaymentMethod: AlternativePaymentMethod;
}

interface AlternativePaymentMethod {
  externalAccountID: string;
  externalAccountDescription: string;
  externalTransactionId: string;
  orderTransactionId: string;
  apmPayerInfo: string;
  paymentMethod: string;
  apmreferenceID: string;
}

export interface NuveiPaymentStatusResponse {
  internalRequestId: number;
  status: 'SUCCESS' | 'ERROR';
  errCode: number;
  gwErrorReason?: string;
  reason: string;
  merchantSiteId: string;
  version: string;
  clientRequestId: string;
  sessionToken: string;
  clientUniqueId: string;
  transactionType: string;
  transactionStatus: 'APPROVED' | 'DECLINED' | 'ERROR' | 'PENDING';
  userTokenId: string;
  transactionId: string;
  paymentOption: PaymentOption;
  currency: string;
  amount: string;
}
